// Service dates on the Rolodex calendar (pages/Calendar.tsx). Each vessel on the Service sheet gives at most
// three entries, made fresh from the sheet every time it loads:
//   • "Service Start Date" on its Start date, or (marked expected) on its Estimated Vessel Arrival Date, else
//     its Expected date, while it has no Start;
//   • "Service Finish Date" on its Finish date;
//   • "Service Delivery Date" on its Delivery date (the handover the yard records far more often than a
//     Finish), never its "Expected delivery date".
// Nothing is stored, so the calendar is always the sheet: a date changed on the Service page (or in
// Smartsheet) moves its entry as soon as the grid refreshes, and a vessel can never be listed twice. Only
// vessel rows count (the rows right under a group); groups and works never make an entry. Dates are plain
// 'YYYY-MM-DD' days, never shifted through a time zone. The month grid and list maths live here too, so
// all of it is tested (serviceCalendar.test.ts).
import { findServiceColumn, MODEL_ORDER, splitBoatTitle } from '@technohull/shared';
import type { SheetGridColumn, SheetGridRow } from './hooks.js';
import { fold } from '../lib/search.js';

export type ServiceDateKind = 'start' | 'finish' | 'delivery';
export const SERVICE_DATE_TITLE: Record<ServiceDateKind, string> = {
  start: 'Service Start Date',
  finish: 'Service Finish Date',
  delivery: 'Service Delivery Date',
};

export interface ServiceDateEntry {
  /** `${vesselId}:${kind}`: one per vessel per kind */
  key: string;
  vesselId: number;
  kind: ServiceDateKind;
  /** the day, 'YYYY-MM-DD' */
  date: string;
  /** a start shown on the vessel's Expected date, because it has no Start date yet */
  expected: boolean;
  /** "Service Start Date" / "Service Finish Date" */
  title: string;
  /** the vessel's type (model), '' when it isn't known */
  model: string;
  /** the vessel's name, without a leading model */
  name: string;
  /** "GT9 · Θαλασσινή (Νίκος Παπαδόπουλος)": the vessel as the calendar names it */
  label: string;
  /** the label without a trailing "(…)" (the owner, a note), for a month cell: "GT9 · Θαλασσινή" */
  short: string;
  sap: string;
  /** the vessel's group heading on the sheet */
  group: string;
}

// ── the vessel's date columns ───────────────────────────────────────────────────────────────
// titled the way the Service page and its Gantt read them; the Expected date column is never a Finish
// (it may be called "Expected finish" / "Expected completion")
const START_TITLE = /start|begin|έναρξ|εναρξ/i;
const FINISH_TITLE = /finish|\bend\b|complet|due|deadline|λήξ|ληξ|ολοκλ|τέλος|τελος|προθεσμ/i;
// the handover itself: "Delivery date" / "Παράδοση", never a planned one ("Expected delivery date")
const DELIVERY_TITLE = /deliver|handover|παράδοσ|παραδοσ/i;
const PLANNED_TITLE = /expect|estimat|plann|εκτιμ|αναμεν|προγραμμ/i;
const isDateCol = (c: SheetGridColumn) => c.type === 'DATE' || c.type === 'DATETIME';

export interface VesselDateColumns { start?: SheetGridColumn; finish?: SheetGridColumn; expected?: SheetGridColumn; delivery?: SheetGridColumn; expectedArrival?: SheetGridColumn }

/** the sheet's Start, Finish, Expected, Delivery and Estimated Vessel Arrival date columns (any of them may
 *  be missing). The arrival dates are never read as any of the others. */
export function vesselDateColumns(cols: readonly SheetGridColumn[]): VesselDateColumns {
  const dates = cols.filter((c) => !c.primary && isDateCol(c));
  const expected = findServiceColumn('expected', dates);
  const expectedArrival = findServiceColumn('expectedArrival', dates);
  const arrival = findServiceColumn('arrival', dates);
  const others = dates.filter((c) => c !== expected && c !== expectedArrival && c !== arrival);
  const start = others.find((c) => START_TITLE.test(c.title));
  const finish = others.find((c) => c !== start && FINISH_TITLE.test(c.title));
  const delivery = others.find((c) => c !== start && c !== finish && DELIVERY_TITLE.test(c.title) && !PLANNED_TITLE.test(c.title));
  return { start, finish, expected, delivery, expectedArrival };
}

/** a cell's day as 'YYYY-MM-DD', or '' when it holds no real date. A DATETIME keeps the date it was written
 *  with: the day is read off the text, never converted through a time zone. */
export function cellDay(v: string | boolean | undefined | null): string {
  if (typeof v !== 'string') return '';
  const m = /^\s*(\d{4})-(\d{2})-(\d{2})(?=$|[T\s])/.exec(v);
  if (!m) return '';
  const y = +m[1]!, mo = +m[2]!, d = +m[3]!;
  const t = new Date(Date.UTC(y, mo - 1, d));
  return t.getUTCFullYear() === y && t.getUTCMonth() === mo - 1 && t.getUTCDate() === d ? `${m[1]}-${m[2]}-${m[3]}` : '';
}

// ── the vessel's type and name ──────────────────────────────────────────────────────────────
const KNOWN_MODELS = new Set<string>(MODEL_ORDER);
/** the digits of a SAP number, so "J9100391" / "JS9100391" / "9100391" all match (as on the Service page) */
export const sapKey = (s: string): string => String(s ?? '').match(/\d{5,}/)?.[0] ?? '';

/** a vessel's type and name. The type is the fleet's model when its SAP matched a boat (as on the Service
 *  card), else a known model leading the name ("GT9 · Θαλασσινή", "GT7_FANDY"); the name loses that lead. */
export function vesselTypeAndName(raw: string, fleetModel = ''): { model: string; name: string } {
  const text = (raw ?? '').trim();
  const split = splitBoatTitle(text);
  const lead = split.name && KNOWN_MODELS.has(split.model.toUpperCase()) ? split : null;
  return { model: fleetModel.trim() || lead?.model.toUpperCase() || '', name: lead ? lead.name : text };
}

const cellText = (v: string | boolean | undefined): string => (typeof v === 'string' ? v : '');
const KIND_ORDER: Record<ServiceDateKind, number> = { start: 0, finish: 1, delivery: 2 };
/** by day, starts before finishes before deliveries, then by vessel */
export function compareEntries(a: ServiceDateEntry, b: ServiceDateEntry): number {
  if (a.date !== b.date) return a.date < b.date ? -1 : 1;
  if (a.kind !== b.kind) return KIND_ORDER[a.kind] - KIND_ORDER[b.kind];
  return a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: 'base' }) || a.vesselId - b.vesselId;
}

/** the calendar entries for a Service grid, in date order. `fleetModels` maps a SAP (its digits) to the
 *  model of the boat in the current season's fleet, when it's known. */
export function serviceDateEntries(
  grid: { columns: readonly SheetGridColumn[]; rows: readonly SheetGridRow[] },
  fleetModels?: ReadonlyMap<string, string>,
): ServiceDateEntry[] {
  const cols = vesselDateColumns(grid.columns);
  if (!cols.start && !cols.finish && !cols.expected && !cols.delivery && !cols.expectedArrival) return [];
  const primary = grid.columns.find((c) => c.primary) ?? grid.columns[0];
  const sapCol = grid.columns.find((c) => !c.primary && /\bsap\b/i.test(c.title));
  const byId = new Map(grid.rows.map((r) => [r.id, r]));
  const parents = new Set<number>();
  for (const r of grid.rows) if (r.parentId != null) parents.add(r.parentId);
  const day = (r: SheetGridRow, c: SheetGridColumn | undefined) => (c ? cellDay(r.cells[c.id]) : '');

  const out: ServiceDateEntry[] = [];
  for (const v of grid.rows) {
    // a vessel sits right under a top-level group row; anything deeper is a work
    const group = v.parentId != null ? byId.get(v.parentId) : undefined;
    if (!group || group.parentId != null) continue;
    const raw = primary ? cellText(v.cells[primary.id]) : '';
    // a blank row with nothing under it, which the Service page hides too
    if (!raw.trim() && !parents.has(v.id)) continue;
    const start = day(v, cols.start);
    // no Start yet: when it's expected (its estimated arrival, else the Expected date the yard used before)
    const expected = start ? '' : day(v, cols.expectedArrival) || day(v, cols.expected);
    const finish = day(v, cols.finish);
    const delivery = day(v, cols.delivery);
    if (!start && !expected && !finish && !delivery) continue;

    const sap = sapCol ? cellText(v.cells[sapCol.id]).trim() : '';
    const { model, name: vesselName } = vesselTypeAndName(raw, fleetModels?.get(sapKey(sap)) ?? '');
    const name = vesselName || '(unnamed)';
    // "(Νίκος Παπαδόπουλος)", "(2024 build)": the full label keeps it, a month cell has no room
    const bare = name.replace(/\s*\([^()]*\)\s*$/, '') || name;
    const both = (n: string) => (model ? `${model} · ${n}` : n);
    const base = { vesselId: v.id, model, name, label: both(name), short: both(bare), sap, group: cellText(primary ? group.cells[primary.id] : undefined).trim() };
    if (start || expected) out.push({ ...base, key: `${v.id}:start`, kind: 'start', date: start || expected, expected: !start, title: SERVICE_DATE_TITLE.start });
    if (finish) out.push({ ...base, key: `${v.id}:finish`, kind: 'finish', date: finish, expected: false, title: SERVICE_DATE_TITLE.finish });
    if (delivery) out.push({ ...base, key: `${v.id}:delivery`, kind: 'delivery', date: delivery, expected: false, title: SERVICE_DATE_TITLE.delivery });
  }
  return out.sort(compareEntries);
}

// ── filters ─────────────────────────────────────────────────────────────────────────────────
export interface EntryFilter {
  starts: boolean;
  finishes: boolean;
  /** left out: deliveries show */
  deliveries?: boolean;
  /** what was typed in the search box (folded here: accents, case and the tonos don't matter) */
  query: string;
}
/** the entries the Starts / Finishes / Deliveries toggles and the vessel search let through. */
export function filterEntries(entries: readonly ServiceDateEntry[], f: EntryFilter): ServiceDateEntry[] {
  const q = fold(f.query.trim());
  return entries.filter((e) =>
    (e.kind === 'start' ? f.starts : e.kind === 'finish' ? f.finishes : f.deliveries ?? true) &&
    (!q || [e.label, e.sap, e.group].some((s) => fold(s).includes(q))));
}

// ── month grid ──────────────────────────────────────────────────────────────────────────────
// whole days as UTC day numbers, so no daylight-saving change can shift a day
const DAY = 86_400_000;
const pad = (n: number) => String(n).padStart(2, '0');
const isoOf = (n: number): string => new Date(n * DAY).toISOString().slice(0, 10);
/** 0 = Monday … 6 = Sunday */
const weekdayOf = (n: number): number => (new Date(n * DAY).getUTCDay() + 6) % 7;
const monthParts = (month: string): [number, number] => {
  const m = /^(\d{4})-(\d{2})/.exec(month);
  return m ? [+m[1]!, +m[2]!] : [1970, 1];
};

/** the 'YYYY-MM' a day falls in */
export const monthOf = (day: string): string => day.slice(0, 7);

/** the month `by` months after `month` (before it when negative), both 'YYYY-MM' */
export function addMonths(month: string, by: number): string {
  const [y, m] = monthParts(month);
  const t = new Date(Date.UTC(y, m - 1 + by, 1));
  return `${t.getUTCFullYear()}-${pad(t.getUTCMonth() + 1)}`;
}

/** the days of a month view as whole Monday-first weeks: from the Monday on or before the 1st to the Sunday
 *  on or after the last day, so 4 to 6 weeks of 7 'YYYY-MM-DD' days. */
export function monthWeeks(month: string): string[][] {
  const [y, m] = monthParts(month);
  const first = Date.UTC(y, m - 1, 1) / DAY;
  const last = Date.UTC(y, m, 0) / DAY;
  const from = first - weekdayOf(first);
  const to = last + 6 - weekdayOf(last);
  const weeks: string[][] = [];
  for (let d = from; d <= to; d += 7) weeks.push(Array.from({ length: 7 }, (_, i) => isoOf(d + i)));
  return weeks;
}

// ── list ────────────────────────────────────────────────────────────────────────────────────
export interface DayEntries { date: string; entries: ServiceDateEntry[] }

/** the entries from `from` to `to` (inclusive) day by day, in order. A day with none is left out, except
 *  `keep` (today, inside the range), which is always listed so the list can say there's nothing on. */
export function entriesByDay(entries: readonly ServiceDateEntry[], from: string, to: string, keep?: string): DayEntries[] {
  const days = new Map<string, ServiceDateEntry[]>();
  for (const e of entries) {
    if (e.date < from || e.date > to) continue;
    const list = days.get(e.date);
    if (list) list.push(e); else days.set(e.date, [e]);
  }
  if (keep && keep >= from && keep <= to && !days.has(keep)) days.set(keep, []);
  return [...days.entries()]
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
    .map(([date, list]) => ({ date, entries: list.sort(compareEntries) }));
}
